/**
 * Folder filter + bulk selection for presets modal
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Preset } from '../shared';

function setsEqualIds(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of b) {
    if (!a.has(id)) return false;
  }
  return true;
}

export function usePresetFolderBulk(presets: Preset[]) {
  const [folderFilterId, setFolderFilterId] = useState<string | null>(null);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [matchListOrder, setMatchListOrder] = useState(true);
  const prevPresetIdsCsvRef = useRef<string>('');
  const prevFolderFilterIdRef = useRef<string | null>(null);
  const presetsRef = useRef(presets);
  presetsRef.current = presets;

  const presetIdsCsv = useMemo(() => presets.map((p) => p.id).join(','), [presets]);

  const filtered = useMemo(() => {
    if (folderFilterId == null) return presets;
    const rows = presets.filter((p) => p.folders?.some((f) => f.id === folderFilterId));
    return [...rows].sort((a, b) => {
      const sa = a.folders?.find((f) => f.id === folderFilterId)?.sortIndex ?? 0;
      const sb = b.folders?.find((f) => f.id === folderFilterId)?.sortIndex ?? 0;
      if (sa !== sb) return sa - sb;
      return (
        (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name)
      );
    });
  }, [presets, folderFilterId]);

  useEffect(() => {
    const list = presetsRef.current;
    const prevFilter = prevFolderFilterIdRef.current;
    prevFolderFilterIdRef.current = folderFilterId;

    if (folderFilterId != null) {
      const next = new Set(
        list.filter((p) => p.folders?.some((f) => f.id === folderFilterId)).map((p) => p.id),
      );
      setBulkSelected((prev) => (setsEqualIds(prev, next) ? prev : next));
      return;
    }

    if (prevFilter != null) {
      const next = new Set(list.map((p) => p.id));
      setBulkSelected((prev) => (setsEqualIds(prev, next) ? prev : next));
      prevPresetIdsCsvRef.current = presetIdsCsv;
      return;
    }

    const prevCsv = prevPresetIdsCsvRef.current;
    prevPresetIdsCsvRef.current = presetIdsCsv;
    const oldIdSet = new Set(prevCsv.split(',').filter(Boolean));
    const allIds = list.map((p) => p.id);

    setBulkSelected((prev) => {
      if (prevCsv === '') {
        const next = new Set(allIds);
        return setsEqualIds(prev, next) ? prev : next;
      }
      const next = new Set<string>();
      for (const id of allIds) {
        const isNew = !oldIdSet.has(id);
        if (isNew) {
          next.add(id);
        } else if (prev.has(id)) {
          next.add(id);
        }
      }
      return setsEqualIds(prev, next) ? prev : next;
    });
  }, [folderFilterId, presetIdsCsv]);

  const toggleBulk = useCallback((id: string) => {
    setBulkSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const toggleBulkGroup = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setBulkSelected((prev) => {
      const allIn = ids.every((id) => prev.has(id));
      const n = new Set(prev);
      if (allIn) for (const id of ids) n.delete(id);
      else for (const id of ids) n.add(id);
      return n;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setBulkSelected(new Set(filtered.map((p) => p.id)));
  }, [filtered]);

  const deselectAll = useCallback(() => {
    setBulkSelected(new Set());
  }, []);

  return {
    folderFilterId,
    setFolderFilterId,
    filtered,
    bulkSelected,
    toggleBulk,
    toggleBulkGroup,
    selectAllVisible,
    deselectAll,
    matchListOrder,
    setMatchListOrder,
  };
}
